import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { HeartHandshake, ShoppingBasket, HandCoins, Lock, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const PURPOSES = [
  { id: "food", t: "Family food support", d: "Food benefits redeemable at authorised TMF vendors.", icon: ShoppingBasket },
  { id: "cash", t: "Emergency household cash assistance", d: "Towards approved urgent household needs.", icon: HandCoins },
  { id: "food_cash", t: "Food and cash support", d: "A combination, subject to programme rules.", icon: HeartHandshake },
];
const PRESETS = [20, 40, 60, 100];
const PERIODS = [{ v: 3, t: "3 months" }, { v: 6, t: "6 months" }, { v: 12, t: "12 months" }, { v: 0, t: "Ongoing until cancelled" }];

const Pill = ({ active, children, ...p }) => (
  <button type="button" {...p} className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${active ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-200 bg-white text-emerald-900 hover:border-emerald-400"}`}>{children}</button>
);

function useQuote(amount) {
  const [q, setQ] = useState(null);
  useEffect(() => {
    if (!amount || amount <= 0) { setQ(null); return undefined; }
    const t = setTimeout(() => { api.post("/sponsorships/quote", { amount_usd: amount }).then(({ data }) => setQ(data)).catch(() => setQ(null)); }, 350);
    return () => clearTimeout(t);
  }, [amount]);
  return q;
}

const Story = () => (
  <div className="lg:col-span-5 text-white">
    <span className="text-xs font-mono uppercase tracking-widest text-amber-300">TEXT-ME-FOOD Family Sponsorship</span>
    <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">Help a Family.<br /><span className="text-amber-300">Restore Hope.</span></h2>
    <p className="mt-5 text-emerald-100/90 text-base leading-relaxed max-w-md">Sponsor a family's food needs or help them through an urgent household challenge. Your support can make a meaningful difference.</p>
    <p className="mt-5 font-display font-semibold text-sm text-emerald-200 tracking-wide">Food support • Emergency cash • Monthly sponsorship</p>
    <img src="/hero/hero-card.jpg" alt="A TMF beneficiary with his benefit card" data-testid="sponsorship-photo" className="mt-8 hidden lg:block w-full max-w-sm h-auto rounded-2xl border-4 border-emerald-800/50" />
    <div className="mt-6 rounded-2xl bg-emerald-900/60 border border-emerald-700/50 p-4 text-sm text-emerald-100/90 flex gap-3" data-testid="sponsorship-allocation-note">
      <Info className="w-5 h-5 shrink-0 text-amber-300" />
      <span>Donors do not choose a specific family. Your gift goes into the TMF Family Sponsorship Pool, and TMF assigns support to eligible families through its beneficiary verification process. We will show each allocation on your sponsorship page once it is confirmed.</span>
    </div>
  </div>
);

export const FamilySponsorship = () => {
  const [freq, setFreq] = useState("monthly");
  const [preset, setPreset] = useState(20);
  const [custom, setCustom] = useState("");
  const [purpose, setPurpose] = useState("food");
  const [period, setPeriod] = useState(3);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const min = freq === "monthly" ? 20 : 10;
  const amount = custom ? Number(custom) : preset;
  const valid = amount >= min;
  const q = useQuote(valid ? amount : 0);
  const purposeT = PURPOSES.find((p) => p.id === purpose).t.replace("Emergency household cash assistance", "Emergency cash");
  const summary = useMemo(() => (freq === "monthly"
    ? `${purposeT} · ${PERIODS.find((p) => p.v === period).t}` : `${purposeT} · one-time gift`), [freq, purposeT, period]);

  const go = async () => {
    if (!valid) { toast.error(`The minimum ${freq === "monthly" ? "monthly sponsorship" : "one-time gift"} is US$${min}.`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sponsorships/checkout", { frequency: freq, amount_usd: amount, purpose, duration_months: period,
        donor_name: name, donor_email: email, consent_recurring: consent, consent_terms: terms, origin_url: window.location.origin });
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <section id="sponsor-a-family" className="scroll-mt-20 bg-emerald-950 tmf-grain" data-testid="family-sponsorship-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <Story />
        <div className="lg:col-span-7 rounded-[1.75rem] bg-white p-6 sm:p-8 shadow-2xl" data-testid="sponsorship-form">
          <h3 className="font-display font-bold text-2xl text-emerald-950">Sponsor a family</h3>
          <p className="text-sm text-gray-500 mt-1">Choose how you would like to help.</p>
          <div className="mt-5 grid grid-cols-2 rounded-full bg-emerald-50 p-1">
            {[["monthly", "Monthly"], ["one_time", "One-time"]].map(([v, t]) => (
              <button key={v} type="button" data-testid={`sponsorship-freq-${v}`} onClick={() => setFreq(v)}
                className={`rounded-full py-2.5 text-sm font-semibold transition-colors ${freq === v ? "bg-emerald-700 text-white" : "text-emerald-800"}`}>{t}</button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <Pill key={p} active={!custom && preset === p} data-testid={`sponsorship-amount-${p}`} onClick={() => { setPreset(p); setCustom(""); }}>
                ${p}<span className="block text-[11px] font-normal opacity-80">{freq === "monthly" ? "per month" : "one-time"}</span>
              </Pill>
            ))}
          </div>
          <div className="mt-2 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">US$</span>
            <Input data-testid="sponsorship-amount-custom" type="number" min={min} placeholder={`Other amount (min $${min})`} value={custom}
              onChange={(e) => setCustom(e.target.value)} className="pl-12 h-11 rounded-xl" />
          </div>
          {custom && !valid && <p className="text-xs text-red-600 mt-1" data-testid="sponsorship-min-error">Minimum is US${min}.</p>}

          <p className="mt-5 text-sm font-semibold text-emerald-950">What would you like to sponsor?</p>
          <div className="mt-2 grid gap-2">
            {PURPOSES.map((p) => (
              <button key={p.id} type="button" data-testid={`sponsorship-purpose-${p.id}`} onClick={() => setPurpose(p.id)}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${purpose === p.id ? "border-emerald-600 bg-emerald-50" : "border-gray-200 hover:border-emerald-300"}`}>
                <p.icon className="w-5 h-5 text-emerald-700 shrink-0" />
                <span><span className="block text-sm font-semibold text-emerald-950">{p.t}</span><span className="block text-xs text-gray-500">{p.d}</span></span>
              </button>
            ))}
          </div>

          {freq === "monthly" && (<>
            <p className="mt-5 text-sm font-semibold text-emerald-950">Preferred sponsorship period</p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PERIODS.map((p) => <Pill key={p.v} active={period === p.v} data-testid={`sponsorship-period-${p.v}`} onClick={() => setPeriod(p.v)}>{p.t}</Pill>)}
            </div>
          </>)}

          <div className="mt-5 grid sm:grid-cols-2 gap-2">
            <Input data-testid="sponsorship-donor-name" placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" />
            <Input data-testid="sponsorship-donor-email" type="email" placeholder="Email for receipts" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl" />
          </div>

          <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="sponsorship-selection">
            <div className="text-xs uppercase tracking-widest text-amber-700 font-mono">Your selection</div>
            <div className="mt-1 font-display font-extrabold text-2xl text-emerald-950" data-testid="sponsorship-selection-amount">${valid ? amount : "—"}{freq === "monthly" ? "/month" : ""}</div>
            <div className="text-sm text-gray-700" data-testid="sponsorship-selection-summary">{summary}</div>
            {q && valid && (
              <div className="mt-2 text-sm text-emerald-900" data-testid="sponsorship-final-charge">
                Final charge: <b>{q.currency === "NGN" ? `₦${Number(q.amount).toLocaleString("en-NG")}` : `US$${q.amount}`}</b>{freq === "monthly" ? " per month" : ""}
                {q.currency === "NGN" && <span className="text-gray-500"> (US$1 = ₦{Number(q.fx_rate).toLocaleString("en-NG")}, today's rate)</span>}
              </div>
            )}
          </div>

          <ul className="mt-4 space-y-1.5 text-xs text-gray-500 leading-relaxed" data-testid="sponsorship-disclosures">
            <li>A $20 monthly sponsorship contributes to a family's food needs but may not cover them all; the benefit delivered follows TMF allocation rules.</li>
            {q?.currency === "NGN" && <li>Your card is charged in Naira. Your bank may apply its own exchange rate or fees for international cards.</li>}
          </ul>

          {freq === "monthly" && (
            <label className="mt-4 flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <Checkbox data-testid="sponsorship-consent-recurring" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
              <span>I authorise TMF to charge my card the amount above every month {period ? `for ${period} months` : "until I cancel"}. I can cancel anytime from my sponsorship page.</span>
            </label>
          )}
          <label className="mt-3 flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
            <Checkbox data-testid="sponsorship-consent-terms" checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5" />
            <span>I understand TMF assigns my support to eligible families from the sponsorship pool.</span>
          </label>

          <Button data-testid="sponsorship-continue-btn" disabled={busy || !valid || !terms || (freq === "monthly" && !consent) || !name || !email} onClick={go}
            className="mt-5 w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800 text-base font-semibold">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" />Continue to secure payment</>}
          </Button>
          <p className="mt-3 text-center text-xs text-gray-500">Secure hosted checkout by Paystack. TMF never stores your card details. <Link to="/sponsorship/manage" className="text-emerald-700 underline" data-testid="sponsorship-manage-link">Manage an existing sponsorship</Link></p>
        </div>
      </div>
    </section>
  );
};
